import React from "react";

import { Route, Routes } from "react-router-dom";

import HomePage from "home/HomePage";
import NotFound from "notFound/NotFound";
import ThemesListPage from "theme/ThemesListPage";
import StatsPage from "stats/StatsPage";
import ThemeEditor from "theme/ThemeEditor";
import TypeSelector from "startup/TypeSelector";
import MyEbox from "../components/eBox/MyEbox";
import OtherEbox from "../components/eBox/OtherEbox";
import EmailComposer from "../components/eBox/widget/EmailComposer";
import CampaignPage from "../components/campaign/CampaignPage";
import CampaignListPage from "../components/campaign/list/CampaignListPage";
import PrivateRoute from "./PrivateRoute";
import {
  ADMIN_ROLE,
  USER_ROLE,
  CAN_CREATE_EMAIL,
  TTP_ADMIN_ROLE,
  TTP_SUPERVISOR_ROLE,
  CAN_CREATE_SMS,
} from "./roles";
import CreateCampaignContainer from "../components/campaign/create/CreateCampaignContainer";
import DashboardPage from "../components/dashboard/DashboardPage";
import CommunityPage from "../components/community/CommunityPage";
import NewsletterHomePage from "../components/ua-newsletter/NewsletterHomePage";
import DragDropEditor from "../components/editor/DragDropEditor";
import BlocksContainer from "../components/blocks/BlocksContainer";
import MailingListsPage from "../components/MailingLists/MailingListsPage";
import GenericEmailPage from "../components/generic-emailings/GenericEmailPage";
import GenericEmail from "../components/generic-emailings/GenericEmail";
import PreviewEmail from "../components/generic-emailings/PreviewEmail";
import TextModelForm from "../components/text-model/TextModelForm";
import DefaultSettings from "../components/settings/DefaultSettings";
import ApprovalSettings from "../components/approval/setting/ApprovalSettings";
import ThemeCategoryList from "../components/settings/theme-category/ThemeCategoryList";
import ApprovalResponseWrapper from "../components/approval/feedback/ApprovalResponse";
import Administration from "../components/settings/Administration";
import CampaignSMSPage from "../components/sms/campaign/CampaignSMSPage";
import CampaignSMSListPage from "../components/sms/CampaignSMSListPage";
import CampaignSMSStats from "../components/sms/stats/CampaignSMSStats";
import SMSBillsListPage from "../components/sms/bills/SMSBillsListPage";
import smsCreditValidationPage from "../components/sms/bills/SMSCreditValidationPage";
import Settings from "../components/settings/Settings";

const urls = {
  index: "/",
  notFound: "*",
  unauthorized: "/unauthorized",
};

const emailing_urls = {
  campaign_list: "/campaigns",
  campaign_type_selector: "/campaign/selector",
  campaign_form: "/campaign/:campaignId?",
  campaign_stats: "/stats/:campaignId",
  compose_email: "/compose-email",

  themes_edit: "/templates/:templateId",
  themes_list: "/templates",
  drag_drop_editor: "/drag-drop/:templateId?",
  blocks_feeds: "/blocks",

  mailing_lists: "/mailing-lists",
  list_subscribers: "/lists/:listId/subscribers",
  embed_form: "/lists/embed-form",

  settings: "/settings",
  approvers_settings: "/approvers-setting",
  theme_category: "/theme-category",
  approval_response: "/approval/:approvalId/:decision",
  send_ezine: "/ezine/:ezineId",
  administration: "/administration",

  //Generic emailing
  emails_list: "/emails",
  email: "/email/:settingId?",
  email_preview: "/email-preview",
  text_models_edit: "/text-model/:textModelId",
  // default_settings: "/default-settings",
  default_settings: "/default-settings/:automationId?",

  //SENT NEWSLETTER CAMPAIGN
  sent_newsLetter_list: "/newsletter",

  //Statistics
  dashboard: "/dashboard",

  //UA newsletter
  newsletter_ua: "/newsletter-ua/:type",
};

const sms_urls = {
  sms_home: "/sms",
  sms_campaign_stats: "/stats-sms/:campaignSMSId",
  sms_campaign_form: "/campaign-sms/:campaignSMSId?",
  sms_campaign_list: "/smsCampaigns",
  sms_bills_list: "/sms_bills",
  sms_credit_validation: "/sms-credit-validation",
};

const ebox_urls = {
  e_box: "/e_box",
  other_ebox: "/other-ebox",
};

const notification_urls = {
  notification_list: "/notifications",
  notification_form: "/notification/:notificationId?",
};

const community_urls = {
  community_form: "/:lng?/community/:communityUrl?/:communityId?",
};

export const appsUrls = {
  EMAILING: emailing_urls,
  SMS: sms_urls,
  EBOX: ebox_urls,
  NOTIFICATION: notification_urls,
};
export const getRoutes = (store) => {
  const isAdminOfSomeCommunities = () => {
    const { auth } = store.getState();
    return (
      auth?.fetched &&
      auth?.user?.clients?.some(
        (client) => client.authorisation.role === "ADMIN",
      )
    );
  };

  const { auth } = store.getState();

  return (
    <Routes>
      {/* Home */}
      <Route
        path={urls.index}
        element={
          auth?.user?.selectedOrganization?.emailingPreferences?.accessType ===
          "UA" ? (
            <NotFound errorPageType="ACCESS_DENIED" />
          ) : (
            <HomePage />
          )
        }
      />

      {/* Campaign List */}
      <Route
        path={emailing_urls.campaign_list}
        element={
          <PrivateRoute
            auth={auth}
            roles={[ADMIN_ROLE, USER_ROLE]}
            component={CampaignListPage}
          />
        }
      />

      {/* Campaign Type Selector */}
      <Route
        path={emailing_urls.campaign_type_selector}
        element={
          <PrivateRoute
            auth={auth}
            roles={ADMIN_ROLE}
            privilege={CAN_CREATE_EMAIL}
            component={TypeSelector}
          />
        }
      />

      {/* Campaign Form */}
      <Route
        path={emailing_urls.campaign_form}
        element={
          <PrivateRoute
            auth={auth}
            roles={ADMIN_ROLE}
            privilege={CAN_CREATE_EMAIL}
            component={CreateCampaignContainer}
          />
        }
      />

      {/* Send ezine */}
      <Route
        path={emailing_urls.send_ezine}
        element={
          <PrivateRoute
            auth={auth}
            roles={ADMIN_ROLE}
            privilege={CAN_CREATE_EMAIL}
            component={CampaignPage}
          />
        }
      />

      {/* Campaign Stats */}
      <Route
        path={emailing_urls.campaign_stats}
        element={
          <PrivateRoute
            auth={auth}
            roles={[ADMIN_ROLE, USER_ROLE]}
            component={StatsPage}
          />
        }
      />

      {/* Compose Email */}
      <Route
        path={emailing_urls.compose_email}
        element={<PrivateRoute auth={auth} component={EmailComposer} />}
      />

      {/* Themes */}
      <Route
        path={emailing_urls.themes_edit}
        element={
          <PrivateRoute
            auth={auth}
            roles={ADMIN_ROLE}
            component={ThemeEditor}
          />
        }
      />

      <Route
        path={emailing_urls.themes_list}
        element={
          <PrivateRoute
            auth={auth}
            roles={ADMIN_ROLE}
            component={ThemesListPage}
          />
        }
      />
      {/* Drag & Drop Editor */}
      <Route
        path={emailing_urls.drag_drop_editor}
        element={
          <PrivateRoute
            auth={auth}
            roles={ADMIN_ROLE}
            privilege={CAN_CREATE_EMAIL}
            component={DragDropEditor}
          />
        }
      />

      {/* Blocks */}
      <Route
        path={emailing_urls.blocks_feeds}
        element={
          <PrivateRoute
            auth={auth}
            roles={ADMIN_ROLE}
            privilege={CAN_CREATE_EMAIL}
            component={BlocksContainer}
          />
        }
      />

      {/* Mailing lists */}
      <Route
        path={emailing_urls.mailing_lists}
        element={
          <PrivateRoute
            auth={auth}
            roles={ADMIN_ROLE}
            component={MailingListsPage}
          />
        }
      />

      {/* Generic Emails */}
      <Route
        path={emailing_urls.emails_list}
        element={
          <PrivateRoute
            auth={auth}
            roles={ADMIN_ROLE}
            component={GenericEmailPage}
          />
        }
      />

      <Route
        path={emailing_urls.email}
        element={
          <PrivateRoute
            auth={auth}
            roles={ADMIN_ROLE}
            component={GenericEmail}
          />
        }
      />

      <Route
        path={emailing_urls.email_preview}
        element={
          <PrivateRoute
            auth={auth}
            roles={ADMIN_ROLE}
            component={PreviewEmail}
          />
        }
      />

      <Route
        path={emailing_urls.text_models_edit}
        element={
          <PrivateRoute
            auth={auth}
            roles={ADMIN_ROLE}
            component={TextModelForm}
          />
        }
      />

      <Route
        path={emailing_urls.default_settings}
        element={
          <PrivateRoute
            auth={auth}
            roles={[ADMIN_ROLE]}
            component={DefaultSettings}
          />
        }
      />

      {/* Settings & Approval */}
      <Route
        path={emailing_urls.settings}
        element={
          <PrivateRoute auth={auth} roles={ADMIN_ROLE} component={Settings} />
        }
      />

      <Route
        path={emailing_urls.approvers_settings}
        element={
          <PrivateRoute
            auth={auth}
            roles={ADMIN_ROLE}
            component={ApprovalSettings}
          />
        }
      />

      <Route
        path={emailing_urls.theme_category}
        element={
          <PrivateRoute
            auth={auth}
            roles={ADMIN_ROLE}
            component={ThemeCategoryList}
          />
        }
      />

      <Route
        path={emailing_urls.approval_response}
        element={<PrivateRoute auth={auth} component={ApprovalResponseWrapper} />}
      />

      <Route
        path={emailing_urls.administration}
        element={
          <PrivateRoute
            auth={auth}
            roles={[TTP_ADMIN_ROLE, TTP_SUPERVISOR_ROLE]}
            component={Administration}
          />
        }
      />

      {/* SMS */}
      <Route path={sms_urls.sms_home} element={<HomePage />} />

      <Route
        path={sms_urls.sms_campaign_form}
        element={
          <PrivateRoute
            auth={auth}
            roles={ADMIN_ROLE}
            privilege={CAN_CREATE_SMS}
            component={CampaignSMSPage}
          />
        }
      />

      <Route
        path={sms_urls.sms_campaign_list}
        element={
          <PrivateRoute
            auth={auth}
            roles={[ADMIN_ROLE, USER_ROLE]}
            component={CampaignSMSListPage}
          />
        }
      />

      <Route
        path={sms_urls.sms_campaign_stats}
        element={
          <PrivateRoute
            auth={auth}
            roles={[ADMIN_ROLE, USER_ROLE]}
            component={CampaignSMSStats}
          />
        }
      />

      <Route
        path={sms_urls.sms_bills_list}
        element={
          <PrivateRoute
            auth={auth}
            roles={ADMIN_ROLE}
            component={SMSBillsListPage}
          />
        }
      />

      <Route
        path={sms_urls.sms_credit_validation}
        element={
          <PrivateRoute
            auth={auth}
            roles={TTP_SUPERVISOR_ROLE}
            component={smsCreditValidationPage}
          />
        }
      />

      {/* Dashboard */}
      <Route
        path={emailing_urls.dashboard}
        element={
          <PrivateRoute
            auth={auth}
            roles={[ADMIN_ROLE, USER_ROLE]}
            component={DashboardPage}
          />
        }
      />

      {/* UA Newsletter */}
      <Route
        path={emailing_urls.newsletter_ua}
        element={<PrivateRoute auth={auth} component={NewsletterHomePage} />}
      />

      {/* Ebox */}
      <Route
        path={ebox_urls.e_box}
        element={<PrivateRoute auth={auth} component={MyEbox} />}
      />

      {isAdminOfSomeCommunities() && (
        <Route
          path={ebox_urls.other_ebox}
          element={<PrivateRoute auth={auth} component={OtherEbox} />}
        />
      )}

      {/* Community */}
      <Route path={community_urls.community_form} element={<CommunityPage />} />

      {/* Unauthorized */}
      <Route
        path={urls.unauthorized}
        element={<NotFound errorPageType="ACCESS_DENIED" />}
      />

      {/* 404 */}
      <Route
        path={urls.notFound}
        element={<NotFound errorPageType="ERROR_404" />}
      />
    </Routes>
  );
};
